import { useTranslation } from '@client-app/i18n';
import GlobalLoading from '@/components/GlobalLoading';
import type { Translate as TranslateFn } from '@client-app/i18n';
import { AUTH_STORAGE_KEY, supabase } from '@common/api/authService';
import {
    featureService,
    type InviteStatusResponse,
} from '@common/api/featureService';
import LogoWithLink from '@common/components/Logo/LogoWithLink';
import { CircleNotch, SignOut, Users, WarningCircle, X } from '@phosphor-icons/react';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildInviteSuccessAdminRoute } from '@/components/Onboarding/inviteReturn';
import {
    getInviteJoinRedirect,
    readStableInviteAutoJoin,
    readStableInviteToken,
    resolveStableInviteJoinAction,
    resolveStableInviteState,
} from './inviteLinkFlow';

const getRoleArticle = (roleName: string) => (
    /^[aeiou]/i.test(roleName.trim()) ? 'an' : 'a'
);

const getWorkspaceInitial = (workspaceName: string) => (
    workspaceName.trim().charAt(0).toUpperCase() || 'W'
);

const formatRoleName = (role?: string | null, t?: TranslateFn) => {
    const value = String(role || '').trim();

    if (!value) {
        return t ? t('website.inviteAccept.memberRole') : 'Member';
    }

    const normalized = value.toUpperCase();
    if (t) {
        if (normalized === 'OWNER') return t('website.membersSettings.ownerRole');
        if (normalized === 'ADMIN') return t('website.membersSettings.adminRole');
        if (normalized === 'MEMBER') return t('website.membersSettings.memberRole');
    }

    return value
        .replace(/[_-]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
        .join(' ');
};

const buildInviteSubtitle = (status: InviteStatusResponse, t: TranslateFn) => {
    if (status.status === 'ACCEPTED') {
        return t('website.inviteAccept.alreadyAccepted');
    }

    const role = formatRoleName(
        status.dataRoleName
        || status.data_role_name
        || status.workspaceRoleName
        || status.workspaceRoleKey
        || status.role,
        t,
    );
    const inviter = status.inviterName || status.inviterEmail || t('website.inviteAccept.teammate');

    return t('website.inviteAccept.singleSubtitle', {
        inviter,
        article: getRoleArticle(role),
        role,
    });
};

const InviteAcceptPage = () => {
    const { t } = useTranslation();
    const token = useMemo(() => readStableInviteToken(window.location.search), []);
    const shouldAutoJoin = useMemo(() => readStableInviteAutoJoin(window.location.search), []);
    const [inviteStatus, setInviteStatus] = useState<InviteStatusResponse | null>(null);
    const [isLoading, setIsLoading] = useState(Boolean(token));
    const [error, setError] = useState<string | null>(null);
    const [joinError, setJoinError] = useState<string | null>(null);
    const [isJoining, setIsJoining] = useState(false);
    const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
    const [shouldShowSwitchAccountAction, setShouldShowSwitchAccountAction] = useState(false);
    const hasAutoJoinStartedRef = useRef(false);
    const pageState = resolveStableInviteState(token, inviteStatus, isLoading, error);
    const shouldShowGlobalInviteLoading = isJoining || (
        shouldAutoJoin &&
        !joinError &&
        !shouldShowSwitchAccountAction &&
        (pageState.state === 'loading' || pageState.state === 'ready')
    );

    useEffect(() => {
        let isCancelled = false;

        if (!token) {
            setIsLoading(false);
            return undefined;
        }

        const loadInvite = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const response = await featureService.getInviteStatus(token);
                if (!isCancelled) {
                    setInviteStatus(response);
                }
            } catch (statusError: any) {
                if (!isCancelled) {
                    setInviteStatus(null);
                    setError(statusError?.message || t('website.inviteAccept.loadFailed'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        void loadInvite();

        return () => {
            isCancelled = true;
        };
    }, [t, token]);

    const handleJoin = useCallback(async () => {
        if (pageState.state !== 'ready' || isJoining) {
            return;
        }

        setIsJoining(true);
        setJoinError(null);
        setShouldShowSwitchAccountAction(false);

        try {
            const sessionResult = await supabase.auth.getSession();
            const joinAction = resolveStableInviteJoinAction(
                pageState.status,
                sessionResult.data.session?.user?.email,
            );

            if (joinAction === 'wrong-account') {
                setShouldShowSwitchAccountAction(true);
                throw new Error(t('website.inviteAccept.differentEmail'));
            }

            if (joinAction === 'accept') {
                const acceptResponse = await featureService.acceptInvitation(token);
                window.location.assign(buildInviteSuccessAdminRoute(acceptResponse.workspaceId));
                return;
            }

            const response = await featureService.startInviteJoin(token);
            const authUrl = getInviteJoinRedirect(response);
            window.location.assign(authUrl);
        } catch (joinError: any) {
            setJoinError(joinError?.message || t('website.inviteAccept.startFailed'));
            setIsJoining(false);
        }
    }, [isJoining, pageState, t, token]);

    useEffect(() => {
        if (!shouldAutoJoin || hasAutoJoinStartedRef.current || pageState.state !== 'ready') {
            return;
        }

        hasAutoJoinStartedRef.current = true;
        void handleJoin();
    }, [handleJoin, pageState.state, shouldAutoJoin]);

    const handleSwitchAccount = async () => {
        setIsSwitchingAccount(true);
        setJoinError(null);
        const redirectTarget = `/admin/auth?redirect=${encodeURIComponent(window.location.href)}`;

        Cookies.remove(AUTH_STORAGE_KEY);
        try {
            await supabase.auth.signOut({ scope: 'local' });
        } catch (err) {
            console.error('Failed to sign out before invite account switch:', err);
        } finally {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = redirectTarget;
        }
    };

    const renderContent = () => {
        if (pageState.state === 'loading') {
            return (
                <div className="flex flex-col items-center gap-4 text-center -translate-y-8 sm:-translate-y-14">
                    <CircleNotch size={32} weight="bold" className="animate-spin text-[#1A162F]" />
                    <p className="text-sm font-normal text-[#6F6A78]">
                        {t('website.inviteAccept.checkingInvitation')}
                    </p>
                </div>
            );
        }

        if (pageState.state === 'ready') {
            const workspaceName = pageState.status.workspaceName || t('website.inviteAccept.thisWorkspace');
            const inviteRoleName = formatRoleName(
                pageState.status.dataRoleName
                || pageState.status.data_role_name
                || pageState.status.workspaceRoleName
                || pageState.status.workspaceRoleKey
                || pageState.status.role,
                t,
            );
            const subtitleSpacingClassName = joinError ? 'mb-4' : 'mb-8';

            return (
                <div className="flex w-full max-w-[540px] flex-col items-center text-center -translate-y-8 sm:-translate-y-14">
                    <section className="invite-letter-card relative w-full overflow-visible rounded-[32px] border border-[#E2E5EC]/60 bg-white px-8 pb-10 pt-14 shadow-[0_30px_80px_rgba(26,22,47,0.06),0_10px_30px_rgba(26,22,47,0.03)] sm:px-14 sm:pb-14 sm:pt-16">
                        {/* Inner dotted border decoration — letter stationery feel */}
                        <div className="pointer-events-none absolute inset-[12px] rounded-[24px] border border-dashed border-[#DFE3EC]/60" />

                        {/* Team Icon — white background with brand color */}
                        <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-[22px] bg-white border border-[#DFE3EC]/70 shadow-[0_8px_20px_rgba(26,22,47,0.04)] ring-4 ring-[#F2F4F8] transition-transform duration-500 hover:rotate-[10deg] cursor-default">
                            <Users size={32} weight="bold" className="text-[#1A162F]" />
                        </div>

                        {/* Title */}
                        <h1 className="mb-2.5 text-[32px] font-medium leading-tight tracking-tight text-[#1A162F]">
                            {t('website.inviteAccept.joinTitle')}
                        </h1>

                        {/* Subtitle */}
                        <p className={`${subtitleSpacingClassName} mx-auto max-w-[420px] whitespace-normal break-words px-2 text-[14.5px] font-normal leading-relaxed text-[#6F6A78] [overflow-wrap:anywhere]`}>
                            {buildInviteSubtitle(pageState.status, t)}
                        </p>

                        {/* Error banner */}
                        {joinError ? (
                            <div className="mx-auto mb-6 flex w-full max-w-[600px] items-start gap-3 rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-left text-[15px] leading-6 text-red-700">
                                <WarningCircle size={20} className="mt-0.5 shrink-0" weight="bold" />
                                <span>{joinError}</span>
                            </div>
                        ) : null}

                        {/* Elegant divider */}
                        <div className="mx-auto mb-8 mt-2 flex w-full max-w-[200px] items-center gap-3">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#DFE3EC]" />
                            <div className="h-1.5 w-1.5 rotate-45 border border-[#C8CDD8] bg-white" />
                            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#DFE3EC]" />
                        </div>

                        {/* Clickable Invitation Card — workspace info + join action */}
                        <button
                            type="button"
                            onClick={handleJoin}
                            disabled={isJoining}
                            className="group relative mx-auto w-full max-w-[420px] cursor-pointer select-none rounded-[22px] border border-[#E8EBF0]/80 bg-white px-6 pb-6 pt-5 shadow-[0_4px_16px_rgba(26,22,47,0.04)] transition-all duration-300 hover:border-brand/35 hover:bg-[#F8F9FC] hover:shadow-[0_20px_40px_rgba(26,22,47,0.12),0_8px_16px_rgba(26,22,47,0.04)] hover:-translate-y-1.5 hover:scale-[1.015] active:translate-y-0 active:scale-100 active:shadow-[0_2px_8px_rgba(26,22,47,0.06)] disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            <div className="text-[18px] font-semibold leading-snug text-[#1A162F] break-all">{workspaceName}</div>
                            <div className="mt-1.5 text-[12px] font-medium text-[#8B8697]">
                                {t('website.inviteAccept.roleLabel')} {inviteRoleName}
                            </div>
                            <div className="mt-5 flex h-[46px] w-full items-center justify-center rounded-full bg-[#1A162F] text-[14px] font-medium text-white shadow-[0_6px_18px_rgba(26,22,47,0.16)] transition-all duration-300 group-hover:bg-[#2B254C] group-hover:shadow-[0_8px_20px_rgba(26,22,47,0.24)]">
                                {isJoining ? (
                                    <>
                                        <CircleNotch size={18} weight="bold" className="mr-2 animate-spin" />
                                        {t('website.inviteAccept.startingLogin')}
                                    </>
                                ) : (
                                    t('website.inviteAccept.join')
                                )}
                            </div>
                        </button>

                        {/* Bottom decorative divider */}
                        <div className="mx-auto mt-10 flex w-full max-w-[260px] items-center gap-2">
                            <div className="h-px flex-1 border-t border-dotted border-[#DFE3EC]" />
                            <div className="flex items-center gap-1">
                                <div className="h-1 w-1 rotate-45 border border-[#C8CDD8]" />
                                <div className="h-1.5 w-1.5 rotate-45 border border-[#B0B7C5] bg-white" />
                                <div className="h-1 w-1 rotate-45 border border-[#C8CDD8]" />
                            </div>
                            <div className="h-px flex-1 border-t border-dotted border-[#DFE3EC]" />
                        </div>
                    </section>

                    {/* Secondary Create Action with expanding underline slide animation */}
                    <div className="mt-6 mb-4">
                        <a
                            href="/admin/onboarding?create_workspace=true"
                            className="relative inline-flex min-h-11 items-center justify-center text-[14px] font-medium text-[#7C869F] transition-all hover:text-[#1A162F] after:absolute after:bottom-2 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:w-8 after:bg-[#1A162F] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-center"
                        >
                            {t('website.inviteAccept.createWorkspace')}
                        </a>
                    </div>
                </div>
            );
        }

        const invalidStatus = pageState.state === 'invalid'
            ? String(pageState.status?.status || '').toUpperCase()
            : '';
        const isCancelledInvite = invalidStatus === 'CANCELLED';
        const message = pageState.state === 'error'
            ? pageState.message
            : isCancelledInvite
                ? t('website.inviteAccept.cancelledDescription')
                : t('website.inviteAccept.invalidDescription');
        const invalidTitle = isCancelledInvite
            ? t('website.inviteAccept.unavailableTitle')
            : t('website.inviteAccept.invalidTitle');

        return (
            <div className="flex w-full max-w-[420px] flex-col items-center text-center -translate-y-8 sm:-translate-y-14">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-[6px] border-red-500 text-red-500">
                    <WarningCircle size={42} weight="bold" />
                </div>
                <h1 className="mb-3 text-[28px] font-medium leading-tight text-[#1A162F]">
                    {invalidTitle}
                </h1>
                <p className="mb-8 text-[15px] leading-6 text-[#5F5968]">
                    {pageState.state === 'missing-token'
                        ? t('website.inviteAccept.missingToken')
                        : message}
                </p>
                <a
                    href="/admin/auth"
                    className="flex h-12 w-full items-center justify-center rounded-xl border border-[#E4E1EA] bg-white px-6 text-[15px] font-medium text-[#1A162F] transition-colors hover:bg-[#F8F7FA]"
                >
                    {t('website.inviteAccept.backToSignIn')}
                </a>
            </div>
        );
    };

    return (
        <div className="relative min-h-screen bg-gradient-to-b from-[#F8F9FA] to-[#EBEEF2] overflow-hidden">
            {/* Mesh Background Ambient Glows */}
            <div className="absolute -left-[10%] top-[10%] w-[380px] h-[380px] rounded-full bg-gradient-to-br from-[#2F7BFF]/10 to-transparent blur-[120px] pointer-events-none select-none" />
            <div className="absolute -right-[10%] bottom-[15%] w-[420px] h-[420px] rounded-full bg-gradient-to-tr from-[#B8D4FF]/12 to-transparent blur-[140px] pointer-events-none select-none" />

            <GlobalLoading isOpen={shouldShowGlobalInviteLoading} />
            <div className="fixed left-8 top-8">
                <LogoWithLink size="26px" />
            </div>
            <div className="fixed top-6 right-6 z-[105] flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => {
                        if (shouldShowSwitchAccountAction) {
                            void handleSwitchAccount();
                            return;
                        }

                        window.location.href = '/admin/auth';
                    }}
                    disabled={shouldShowSwitchAccountAction && isSwitchingAccount}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-all disabled:cursor-wait disabled:opacity-60"
                >
                    {shouldShowSwitchAccountAction ? (
                        isSwitchingAccount ? (
                            <CircleNotch size={16} className="animate-spin text-[#1A162F]" weight="bold" />
                        ) : (
                            <SignOut size={16} className="text-[#1A162F]" weight="bold" />
                        )
                    ) : (
                        <X size={16} className="text-[#1A162F]" weight="bold" />
                    )}
                    <span className="text-[13px] font-medium text-[#1A162F]">
                        {shouldShowSwitchAccountAction
                            ? t('website.inviteAccept.logOut')
                            : t('website.inviteAccept.close')}
                    </span>
                </button>
            </div>
            <main className="flex min-h-screen items-center justify-center px-5 py-28 sm:px-6 sm:py-32">
                {renderContent()}
            </main>

        </div>
    );
};

export default InviteAcceptPage;
